import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Flame, Calendar, TrendingUp, Plus, Sparkles, Clock, 
  CheckCircle2, ArrowRight, Trophy, Zap, BarChart3,
  Facebook, Instagram, Twitter, Youtube, Share2, Gift,
  Eye, Heart, Target
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// Best posting times by platform (based on general social media research)
const BEST_POSTING_TIMES = {
  instagram: { day: 'Tuesday-Friday', time: '11 AM - 1 PM', peak: '11 AM' },
  tiktok: { day: 'Tuesday-Thursday', time: '7 PM - 9 PM', peak: '7 PM' },
  facebook: { day: 'Wednesday-Friday', time: '1 PM - 4 PM', peak: '1 PM' },
  twitter: { day: 'Monday-Thursday', time: '8 AM - 10 AM', peak: '9 AM' },
  youtube: { day: 'Thursday-Saturday', time: '12 PM - 3 PM', peak: '2 PM' },
};

const DailyDropDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    streak: 0,
    postsThisWeek: 0,
    totalPosts: 0,
    hasPostedToday: false
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [userRes, statsRes, postsRes, analyticsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/daily-drop/stats'),
        api.get('/daily-drop/posts/recent'),
        api.get('/daily-drop/analytics?range=7d').catch(() => ({ data: null }))
      ]);
      setUser(userRes.data);
      setStats(statsRes.data);
      setRecentPosts(postsRes.data.posts || []);
      setAnalytics(analyticsRes.data);
      
      // Redirect to onboarding if not completed
      if (!statsRes.data.onboardingCompleted) {
        navigate('/daily-drop/onboarding');
        return;
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStreakMessage = () => {
    if (stats.streak === 0) return "Start your streak today! 🚀";
    if (stats.streak < 7) return `You're on a ${stats.streak}-day streak. Keep it going! 🔥`;
    if (stats.streak < 30) return `Amazing ${stats.streak}-day streak! You're building momentum! 💪`;
    if (stats.streak < 100) return `Incredible ${stats.streak}-day streak! Consistency builds empires! 🏆`;
    return `LEGENDARY ${stats.streak}-day streak! You're unstoppable! 💎`;
  };

  const getStreakBadge = () => {
    if (stats.streak >= 100) return { icon: '💎', label: '100 Day Legend' };
    if (stats.streak >= 30) return { icon: '🏆', label: '30 Day Champion' };
    if (stats.streak >= 7) return { icon: '🔥', label: '7 Day Streak' };
    return null;
  };

  const streakBadge = getStreakBadge();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-r from-purple-600 to-amber-500 p-2 rounded-lg">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">DailyDrop AI</h1>
              <p className="text-xs text-purple-300">Powered by TerryLBPrompts.com</p>
            </div>
          </div>
          <nav className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop/create')}
              data-testid="nav-create"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create
            </Button>
            <Button 
              variant="ghost" 
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop/schedule')}
              data-testid="nav-schedule"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule
            </Button>
            <Button 
              variant="ghost" 
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop/analytics')}
              data-testid="nav-analytics"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </Button>
            <Button 
              variant="ghost" 
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop/settings')}
              data-testid="nav-settings"
            >
              Settings
            </Button>
            <Button 
              variant="ghost" 
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/dashboard')}
              data-testid="nav-back"
            >
              Back
            </Button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-2">
            Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-amber-400">{user?.name?.split(' ')[0] || 'Creator'}</span>
          </h2>
          <p className="text-purple-200 text-lg">{getStreakMessage()}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* Streak Card */}
          <Card className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border-orange-500/30 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-200 text-sm font-medium">Current Streak</p>
                  <p className="text-4xl font-bold text-white">{stats.streak}</p>
                  <p className="text-orange-300 text-xs">days</p>
                </div>
                <div className="bg-orange-500/30 p-3 rounded-full">
                  <Flame className="h-8 w-8 text-orange-400" />
                </div>
              </div>
              {streakBadge && (
                <Badge className="mt-3 bg-orange-500/30 text-orange-200 border-orange-500/50">
                  {streakBadge.icon} {streakBadge.label}
                </Badge>
              )}
            </CardContent>
          </Card>

          {/* Today's Status */}
          <Card className={`border backdrop-blur-sm ${stats.hasPostedToday ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30' : 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${stats.hasPostedToday ? 'text-green-200' : 'text-purple-200'}`}>Today's Drop</p>
                  <p className="text-2xl font-bold text-white">{stats.hasPostedToday ? 'Complete!' : 'Pending'}</p>
                </div>
                <div className={`p-3 rounded-full ${stats.hasPostedToday ? 'bg-green-500/30' : 'bg-purple-500/30'}`}>
                  {stats.hasPostedToday ? (
                    <CheckCircle2 className="h-8 w-8 text-green-400" />
                  ) : (
                    <Clock className="h-8 w-8 text-purple-400" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Posts This Week */}
          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-200 text-sm font-medium">This Week</p>
                  <p className="text-4xl font-bold text-white">{stats.postsThisWeek}</p>
                  <p className="text-blue-300 text-xs">posts</p>
                </div>
                <div className="bg-blue-500/30 p-3 rounded-full">
                  <Calendar className="h-8 w-8 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Posts */}
          <Card className="bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border-amber-500/30 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-200 text-sm font-medium">Total Drops</p>
                  <p className="text-4xl font-bold text-white">{stats.totalPosts}</p>
                  <p className="text-amber-300 text-xs">all time</p>
                </div>
                <div className="bg-amber-500/30 p-3 rounded-full">
                  <TrendingUp className="h-8 w-8 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Create Post Card */}
          <Card className="bg-gradient-to-br from-purple-600/30 to-pink-600/30 border-purple-500/30 backdrop-blur-sm hover:border-purple-400/50 transition-all cursor-pointer group"
                onClick={() => navigate('/daily-drop/create')}>
            <CardContent className="p-8">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-xl group-hover:scale-110 transition-transform">
                  <Plus className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">Create Your Daily Drop</h3>
                  <p className="text-purple-200">
                    {stats.hasPostedToday 
                      ? "You've already dropped today. Schedule for tomorrow?" 
                      : "Don't break your streak! Create today's post now."}
                  </p>
                </div>
                <ArrowRight className="h-6 w-6 text-purple-300 group-hover:translate-x-2 transition-transform" />
              </div>
            </CardContent>
          </Card>

          {/* AI Generator Card */}
          <Card className="bg-gradient-to-br from-amber-600/30 to-orange-600/30 border-amber-500/30 backdrop-blur-sm hover:border-amber-400/50 transition-all cursor-pointer group"
                onClick={() => navigate('/daily-drop/ai-generator')}>
            <CardContent className="p-8">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 rounded-xl group-hover:scale-110 transition-transform">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">AI Caption Generator</h3>
                  <p className="text-amber-200">Generate viral captions, hashtags, and hooks instantly</p>
                </div>
                <ArrowRight className="h-6 w-6 text-amber-300 group-hover:translate-x-2 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Best Posting Times & Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Best Posting Times */}
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Clock className="h-5 w-5 text-cyan-400" />
                Best Times to Post
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(BEST_POSTING_TIMES).slice(0, 4).map(([platform, times]) => (
                  <div key={platform} className="flex items-center justify-between p-2 bg-purple-500/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      {platform === 'instagram' && <Instagram className="h-4 w-4 text-pink-400" />}
                      {platform === 'tiktok' && <span className="text-sm">📱</span>}
                      {platform === 'facebook' && <Facebook className="h-4 w-4 text-blue-400" />}
                      {platform === 'twitter' && <Twitter className="h-4 w-4 text-gray-400" />}
                      {platform === 'youtube' && <Youtube className="h-4 w-4 text-red-400" />}
                      <span className="text-purple-200 text-sm capitalize">{platform}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-white text-sm font-medium">{times.peak}</span>
                      <span className="text-purple-300/60 text-xs ml-2">{times.day}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-purple-300/50 text-xs text-center mt-3">
                Based on general engagement patterns
              </p>
            </CardContent>
          </Card>

          {/* Quick Stats Preview */}
          <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center justify-between text-base">
                <span className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-green-400" />
                  Quick Stats (7 days)
                </span>
                <Button 
                  variant="link" 
                  className="text-purple-400 text-xs p-0 h-auto"
                  onClick={() => navigate('/daily-drop/analytics')}
                >
                  View All
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-lg border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Eye className="h-4 w-4 text-blue-400" />
                    <span className="text-blue-200 text-xs">Reach</span>
                  </div>
                  <p className="text-xl font-bold text-white">
                    {analytics?.overview?.totalReach?.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-pink-500/20 to-red-500/20 p-3 rounded-lg border border-pink-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Heart className="h-4 w-4 text-pink-400" />
                    <span className="text-pink-200 text-xs">Engagement</span>
                  </div>
                  <p className="text-xl font-bold text-white">
                    {analytics?.overview?.totalEngagement?.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 p-3 rounded-lg border border-green-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="h-4 w-4 text-green-400" />
                    <span className="text-green-200 text-xs">Eng. Rate</span>
                  </div>
                  <p className="text-xl font-bold text-white">
                    {analytics?.overview?.avgEngagementRate || '0'}%
                  </p>
                </div>
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 p-3 rounded-lg border border-amber-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy className="h-4 w-4 text-amber-400" />
                    <span className="text-amber-200 text-xs">Best Streak</span>
                  </div>
                  <p className="text-xl font-bold text-white">
                    {stats.bestStreak || analytics?.overview?.bestStreak || '0'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Connected Platforms */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Share2 className="h-5 w-5 text-purple-400" />
              Connected Platforms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { name: 'Facebook', icon: Facebook, color: 'from-blue-600 to-blue-500', connected: false },
                { name: 'Instagram', icon: Instagram, color: 'from-pink-600 to-purple-600', connected: false },
                { name: 'X (Twitter)', icon: Twitter, color: 'from-gray-700 to-gray-600', connected: false },
                { name: 'YouTube', icon: Youtube, color: 'from-red-600 to-red-500', connected: false },
                { name: 'TikTok', icon: null, customIcon: <span className="text-lg">📱</span>, color: 'from-gray-800 to-gray-700', connected: false },
              ].map((platform) => {
                const IconComponent = platform.icon;
                return (
                <div key={platform.name} className="relative">
                  <Button
                    variant="outline"
                    className={`w-full h-20 flex flex-col items-center justify-center gap-2 border-2 ${
                      platform.connected 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10'
                    }`}
                    onClick={() => toast.info(`${platform.name} integration coming soon!`)}
                  >
                    <div className={`bg-gradient-to-r ${platform.color} p-2 rounded-lg`}>
                      {IconComponent ? <IconComponent className="h-5 w-5 text-white" /> : platform.customIcon}
                    </div>
                    <span className="text-xs text-purple-200">{platform.name}</span>
                  </Button>
                  {platform.connected && (
                    <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1">
                      <CheckCircle2 className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>
              );})}
            </div>
            <p className="text-center text-purple-300/60 text-sm mt-4">
              Connect your accounts to auto-distribute your daily drops
            </p>
          </CardContent>
        </Card>

        {/* Recent Posts */}
        <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-400" />
                Recent Drops
              </span>
              <Button variant="link" className="text-purple-400" onClick={() => navigate('/daily-drop/schedule')}>
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentPosts.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-purple-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-10 w-10 text-purple-400" />
                </div>
                <p className="text-purple-200 mb-4">No drops yet. Start your journey!</p>
                <Button 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                  onClick={() => navigate('/daily-drop/create')}
                >
                  Create Your First Drop
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentPosts.map((post, index) => (
                  <div key={post.id || index} className="flex items-center gap-4 p-4 bg-purple-500/10 rounded-lg">
                    {post.media_url ? (
                      <img src={post.media_url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    ) : (
                      <div className="w-16 h-16 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <Sparkles className="h-6 w-6 text-purple-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{post.caption?.substring(0, 60) || 'No caption'}...</p>
                      <p className="text-purple-300 text-sm">{new Date(post.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      {post.platforms?.map((p) => (
                        <Badge key={p} variant="outline" className="text-purple-300 border-purple-500/30">
                          {p}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referral Banner */}
        <Card 
          className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30 backdrop-blur-sm cursor-pointer hover:border-amber-400/50 transition-all"
          onClick={() => navigate('/daily-drop/referrals')}
          data-testid="referral-card"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-amber-500/30 p-3 rounded-xl">
                  <Gift className="h-6 w-6 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Earn Free Credits!</h3>
                  <p className="text-amber-200/70 text-sm">Invite friends & earn rewards for every referral</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-amber-300" />
            </div>
          </CardContent>
        </Card>

        {/* Motivational Footer */}
        <div className="mt-8 text-center">
          <p className="text-purple-300/60 text-sm">
            "Create once. Drop everywhere. Grow daily."
          </p>
          <p className="text-purple-400/40 text-xs mt-2">
            DailyDrop AI • Powered by TerryLBPrompts.com
          </p>
        </div>
      </main>
    </div>
  );
};

export default DailyDropDashboard;
